import { useEffect, useMemo, useState } from 'react';
import {
    getSurveys,
    adminLogin,
    createSurvey,
    updateSurvey,
    deleteSurvey,
    getVisitors,
} from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const empty = {
    title: '',
    description: '',
    url: '',
    provider: 'TopSurveys',
    category: 'استطلاعات',
    reward: '10$',
    duration: '10 دقائق',
    image_url: '',
    is_featured: false,
    order: 0,
};

export default function Admin() {
    const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
    const [password, setPassword] = useState('');
    const [surveys, setSurveys] = useState([]);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('dashboard');
    const [visitors, setVisitors] = useState({ log: null, summary: [] });

    const refresh = async () => {
        const list = await getSurveys();
        setSurveys(list);
    };

    const refreshVisitors = async () => {
        try {
            const data = await getVisitors(token);
            setVisitors(data);
        } catch {
            toast.error('تعذّر جلب سجلّ الزوّار');
        }
    };

    useEffect(() => {
        if (token) {
            refresh();
            refreshVisitors();
        }
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const t = await adminLogin(password);
            localStorage.setItem('admin_token', t);
            setToken(t);
            toast.success('تم تسجيل الدخول');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'فشل تسجيل الدخول');
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('secret_unlocked_v1');
        setToken('');
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (editingId) {
                await updateSurvey(token, editingId, form);
                toast.success('تم التحديث');
            } else {
                await createSurvey(token, form);
                toast.success('تم الإضافة');
            }
            setForm(empty);
            setEditingId(null);
            await refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'حدث خطأ');
        } finally {
            setLoading(false);
        }
    };

    const edit = (s) => {
        setEditingId(s.id);
        setForm({
            title: s.title,
            description: s.description,
            url: s.url,
            provider: s.provider,
            category: s.category,
            reward: s.reward,
            duration: s.duration,
            image_url: s.image_url || '',
            is_featured: s.is_featured,
            order: s.order,
        });
        setTab('surveys');
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
    };

    const remove = async (id) => {
        if (!window.confirm('هل أنت متأكد من الحذف؟')) return;
        try {
            await deleteSurvey(token, id);
            toast.success('تم الحذف');
            await refresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'حدث خطأ');
        }
    };

    if (!token) {
        return <LoginScreen {...{ login, password, setPassword, loading }} />;
    }

    return (
        <div className="min-h-screen bg-sand-100 text-ink" data-testid="admin-dashboard">
            <AdminHeader logout={logout} tab={tab} />
            <AdminTabs tab={tab} setTab={setTab} surveys={surveys} visitors={visitors} />

            <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
                {tab === 'dashboard' && (
                    <DashboardTab surveys={surveys} visitors={visitors} onGo={setTab} />
                )}
                {tab === 'surveys' && (
                    <SurveysTab
                        {...{
                            form,
                            setForm,
                            submit,
                            loading,
                            editingId,
                            setEditingId,
                            surveys,
                            edit,
                            remove,
                        }}
                    />
                )}
                {tab === 'visitors' && (
                    <VisitorsPanel visitors={visitors} onRefresh={refreshVisitors} />
                )}
            </div>
        </div>
    );
}

/* ---------- Login Screen ---------- */
function LoginScreen({ login, password, setPassword, loading }) {
    return (
        <div className="min-h-screen bg-sand-100 text-ink flex items-center justify-center px-6">
            <form
                onSubmit={login}
                className="w-full max-w-md border border-ink bg-sand-100 p-8 md:p-12"
                data-testid="admin-login-form"
            >
                <div className="text-xs uppercase tracking-[0.3em] mb-4">
                    — لوحة الإدارة
                </div>
                <h1 className="font-display font-black text-4xl md:text-5xl mb-8 tracking-tight">
                    تسجيل الدخول
                </h1>
                <label className="block text-xs uppercase tracking-widest mb-2">
                    كلمة المرور
                </label>
                <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border border-ink bg-transparent px-4 py-3 mb-6 focus:outline-none focus:bg-sand-200"
                    data-testid="admin-password-input"
                    required
                />
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-ink text-sand-100 py-3 hover:bg-emerald-signal hover:text-ink transition-colors"
                    data-testid="admin-login-submit"
                >
                    {loading ? 'جاري…' : 'دخول'}
                </button>
                <Link
                    to="/"
                    className="block text-center text-xs text-ink-400 mt-6 hover:text-ink"
                >
                    ← العودة للموقع
                </Link>
            </form>
        </div>
    );
}

/* ---------- Header ---------- */
function AdminHeader({ logout, tab }) {
    const title = {
        dashboard: 'نظرة عامة',
        surveys: 'إدارة الاستطلاعات',
        visitors: 'سجلّ الزوّار',
    }[tab];
    return (
        <header className="border-b border-ink bg-sand-100 sticky top-0 z-30">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-6">
                <Link to="/" className="font-display font-black text-xl tracking-tight">
                    استطلاعات<span className="text-emerald-signal">.</span>
                </Link>
                <div className="hidden md:block text-sm text-ink-400">
                    <span className="uppercase tracking-widest text-[10px]">
                        لوحة الإدارة /
                    </span>{' '}
                    <span className="text-ink">{title}</span>
                </div>
                <div className="flex items-center gap-3">
                    <Link
                        to="/"
                        className="text-xs border border-ink px-3 py-2 hover:bg-ink hover:text-sand-100 transition-colors"
                    >
                        الموقع
                    </Link>
                    <button
                        onClick={logout}
                        className="text-xs border border-red-600 text-red-600 px-3 py-2 hover:bg-red-600 hover:text-sand-100 transition-colors"
                        data-testid="admin-logout"
                    >
                        خروج
                    </button>
                </div>
            </div>
        </header>
    );
}

/* ---------- Tabs ---------- */
function AdminTabs({ tab, setTab, surveys, visitors }) {
    const items = [
        { id: 'dashboard', label: 'نظرة عامة', badge: null },
        { id: 'surveys', label: 'الاستطلاعات', badge: surveys.length },
        {
            id: 'visitors',
            label: 'الزوّار',
            badge: visitors?.log?.unique_ips ?? null,
        },
    ];
    return (
        <div className="border-b border-ink/20 bg-sand-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex gap-8 overflow-x-auto no-scrollbar">
                    {items.map((it) => (
                        <button
                            key={it.id}
                            onClick={() => setTab(it.id)}
                            className={`py-4 whitespace-nowrap text-sm border-b-2 transition-colors inline-flex items-center gap-2 ${
                                tab === it.id
                                    ? 'border-ink text-ink font-medium'
                                    : 'border-transparent text-ink-400 hover:text-ink'
                            }`}
                            data-testid={`admin-tab-${it.id}`}
                        >
                            {it.label}
                            {it.badge !== null && (
                                <span
                                    className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[10px] tabular-nums ${
                                        tab === it.id
                                            ? 'bg-ink text-sand-100'
                                            : 'bg-sand-200 text-ink'
                                    }`}
                                >
                                    {it.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------- Dashboard Overview ---------- */
function DashboardTab({ surveys, visitors, onGo }) {
    const featured = surveys.find((s) => s.is_featured);
    const categories = useMemo(() => {
        const counts = {};
        surveys.forEach((s) => {
            counts[s.category] = (counts[s.category] || 0) + 1;
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]);
    }, [surveys]);
    const totalVisits = visitors?.log?.total ?? 0;
    const uniqueIps = visitors?.log?.unique_ips ?? 0;

    return (
        <div className="py-10 space-y-10">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-ink-400 mb-2">
                        — نظرة عامة
                    </div>
                    <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight">
                        أهلاً بك في اللوحة
                    </h2>
                </div>
                <div className="text-sm text-ink-600">
                    {new Date().toLocaleDateString('ar-EG', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                    label="استطلاع"
                    value={surveys.length}
                    accent
                    testid="stat-surveys"
                    onClick={() => onGo('surveys')}
                />
                <StatCard
                    label="فئة"
                    value={categories.length}
                    testid="stat-categories"
                />
                <StatCard
                    label="إجمالي الزيارات"
                    value={totalVisits}
                    testid="stat-visits"
                    onClick={() => onGo('visitors')}
                />
                <StatCard
                    label="IP فريد"
                    value={uniqueIps}
                    testid="stat-unique"
                    accent
                    onClick={() => onGo('visitors')}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Featured card */}
                <div className="lg:col-span-2 border border-ink p-6 md:p-8">
                    <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                        <div className="text-[10px] uppercase tracking-widest text-emerald-signal">
                            العرض المميّز الحالي
                        </div>
                        <button
                            onClick={() => onGo('surveys')}
                            className="text-xs underline hover:text-emerald-signal"
                        >
                            تعديل →
                        </button>
                    </div>
                    {featured ? (
                        <>
                            <div className="font-display font-black text-2xl md:text-3xl leading-tight">
                                {featured.title}
                            </div>
                            <div className="text-sm text-ink-600 mt-3 line-clamp-2">
                                {featured.description}
                            </div>
                            <div className="mt-6 flex flex-wrap gap-6 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-ink-400">
                                        المكافأة
                                    </div>
                                    <div className="font-display font-black text-xl text-emerald-signal mt-1">
                                        {featured.reward}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest text-ink-400">
                                        المنصة
                                    </div>
                                    <div className="font-display font-black text-xl mt-1">
                                        {featured.provider}
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-ink-400 text-sm">
                            لا يوجد استطلاع مميّز حاليًا.
                        </div>
                    )}
                </div>

                {/* Categories breakdown */}
                <div className="border border-ink p-6">
                    <div className="text-[10px] uppercase tracking-widest text-ink-400 mb-4">
                        توزيع الفئات
                    </div>
                    <div className="space-y-3">
                        {categories.length === 0 && (
                            <div className="text-ink-400 text-sm">
                                لا توجد فئات بعد.
                            </div>
                        )}
                        {categories.map(([cat, n]) => {
                            const pct = Math.round((n / surveys.length) * 100);
                            return (
                                <div key={cat}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span>{cat}</span>
                                        <span className="font-mono text-ink-400">
                                            {n} · {pct}%
                                        </span>
                                    </div>
                                    <div className="h-1.5 bg-sand-200 overflow-hidden">
                                        <div
                                            className="h-full bg-emerald-signal"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Recent surveys list */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-black text-xl">
                        آخر الاستطلاعات
                    </h3>
                    <button
                        onClick={() => onGo('surveys')}
                        className="text-xs underline hover:text-emerald-signal"
                    >
                        عرض الكل →
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {surveys.slice(0, 6).map((s) => (
                        <div
                            key={s.id}
                            className="border border-ink p-4 hover:bg-sand-200 transition-colors"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] uppercase tracking-widest bg-ink text-sand-100 px-2 py-0.5">
                                    {s.category}
                                </span>
                                {s.is_featured && (
                                    <span className="text-[10px] uppercase tracking-widest bg-emerald-signal text-ink px-2 py-0.5">
                                        مميّز
                                    </span>
                                )}
                            </div>
                            <div className="font-display font-black text-base line-clamp-1">
                                {s.title}
                            </div>
                            <div className="text-xs text-emerald-signal mt-1 font-medium">
                                {s.reward}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, accent, testid, onClick }) {
    const Comp = onClick ? 'button' : 'div';
    return (
        <Comp
            onClick={onClick}
            data-testid={testid}
            className={`text-start border border-ink p-5 transition-colors ${
                onClick ? 'hover:bg-sand-200 cursor-pointer' : ''
            }`}
        >
            <div className="text-[10px] uppercase tracking-widest text-ink-400">
                {label}
            </div>
            <div
                className={`font-display font-black text-4xl md:text-5xl tabular-nums mt-2 ${
                    accent ? 'text-emerald-signal' : ''
                }`}
            >
                {value}
            </div>
        </Comp>
    );
}

/* ---------- Surveys Tab ---------- */
function SurveysTab({
    form,
    setForm,
    submit,
    loading,
    editingId,
    setEditingId,
    surveys,
    edit,
    remove,
}) {
    return (
        <div className="py-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form */}
            <form
                onSubmit={submit}
                className="lg:col-span-2 lg:sticky lg:top-32 self-start border border-ink p-6 md:p-8 space-y-4"
                data-testid="admin-form"
            >
                <div className="flex items-center justify-between mb-2">
                    <h2 className="font-display font-black text-2xl md:text-3xl">
                        {editingId ? 'تعديل استطلاع' : 'إضافة استطلاع'}
                    </h2>
                    {editingId && (
                        <span className="text-[10px] uppercase tracking-widest bg-emerald-signal text-ink px-2 py-0.5">
                            تعديل
                        </span>
                    )}
                </div>
                <Field
                    label="العنوان"
                    value={form.title}
                    onChange={(v) => setForm({ ...form, title: v })}
                    required
                    testid="input-title"
                />
                <Field
                    label="الوصف"
                    textarea
                    value={form.description}
                    onChange={(v) => setForm({ ...form, description: v })}
                    required
                    testid="input-description"
                />
                <Field
                    label="الرابط (URL)"
                    value={form.url}
                    onChange={(v) => setForm({ ...form, url: v })}
                    required
                    testid="input-url"
                />
                <div className="grid grid-cols-2 gap-3">
                    <Field
                        label="المكافأة"
                        value={form.reward}
                        onChange={(v) => setForm({ ...form, reward: v })}
                        testid="input-reward"
                    />
                    <Field
                        label="المدة"
                        value={form.duration}
                        onChange={(v) => setForm({ ...form, duration: v })}
                        testid="input-duration"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field
                        label="المنصة"
                        value={form.provider}
                        onChange={(v) => setForm({ ...form, provider: v })}
                        testid="input-provider"
                    />
                    <Field
                        label="الفئة"
                        value={form.category}
                        onChange={(v) => setForm({ ...form, category: v })}
                        testid="input-category"
                    />
                </div>
                <Field
                    label="رابط الصورة"
                    value={form.image_url}
                    onChange={(v) => setForm({ ...form, image_url: v })}
                    testid="input-image"
                />
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.is_featured}
                        onChange={(e) =>
                            setForm({ ...form, is_featured: e.target.checked })
                        }
                        data-testid="input-featured"
                        className="w-4 h-4 accent-emerald-signal"
                    />
                    <span>عرض مميّز</span>
                </label>
                <div className="flex gap-3 pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-ink text-sand-100 py-3 hover:bg-emerald-signal hover:text-ink transition-colors font-medium"
                        data-testid="admin-submit"
                    >
                        {loading ? 'جاري…' : editingId ? 'حفظ التعديل' : 'إضافة'}
                    </button>
                    {editingId && (
                        <button
                            type="button"
                            onClick={() => {
                                setForm(empty);
                                setEditingId(null);
                            }}
                            className="px-4 border border-ink hover:bg-ink hover:text-sand-100"
                            data-testid="admin-cancel"
                        >
                            إلغاء
                        </button>
                    )}
                </div>
            </form>

            {/* List */}
            <div className="lg:col-span-3">
                <div className="flex items-baseline justify-between mb-4">
                    <h2 className="font-display font-black text-2xl md:text-3xl">
                        الاستطلاعات
                    </h2>
                    <span className="text-sm text-ink-400">
                        {surveys.length} عنصر
                    </span>
                </div>
                <div className="space-y-3">
                    {surveys.map((s) => (
                        <div
                            key={s.id}
                            className="border border-ink p-4 md:p-5 flex items-start gap-4 hover:bg-sand-200 transition-colors"
                            data-testid={`admin-item-${s.id}`}
                        >
                            {s.image_url ? (
                                <img
                                    src={s.image_url}
                                    alt=""
                                    className="w-20 h-20 object-cover border border-ink shrink-0 grayscale"
                                />
                            ) : (
                                <div className="w-20 h-20 border border-ink bg-ink text-sand-100 flex items-center justify-center font-display font-black text-2xl shrink-0">
                                    {s.provider?.[0] || 'S'}
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-widest bg-ink text-sand-100 px-2 py-0.5">
                                        {s.category}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-widest border border-ink px-2 py-0.5">
                                        {s.provider}
                                    </span>
                                    {s.is_featured && (
                                        <span className="text-[10px] uppercase tracking-widest bg-emerald-signal text-ink px-2 py-0.5">
                                            مميّز
                                        </span>
                                    )}
                                </div>
                                <div className="font-display font-black text-base md:text-lg leading-tight line-clamp-1">
                                    {s.title}
                                </div>
                                <div
                                    className="text-xs text-ink-400 truncate mt-1"
                                    dir="ltr"
                                >
                                    {s.url}
                                </div>
                                <div className="flex gap-4 mt-2 text-xs text-ink-600">
                                    <span className="text-emerald-signal font-medium">
                                        {s.reward}
                                    </span>
                                    <span>·</span>
                                    <span>{s.duration}</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2 shrink-0">
                                <button
                                    onClick={() => edit(s)}
                                    className="text-xs border border-ink px-3 py-1 hover:bg-ink hover:text-sand-100 transition-colors"
                                    data-testid={`admin-edit-${s.id}`}
                                >
                                    تعديل
                                </button>
                                <button
                                    onClick={() => remove(s.id)}
                                    className="text-xs border border-red-600 text-red-600 px-3 py-1 hover:bg-red-600 hover:text-sand-100 transition-colors"
                                    data-testid={`admin-delete-${s.id}`}
                                >
                                    حذف
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, required, textarea, testid }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-widest mb-1.5 text-ink-400">
                {label}
            </label>
            {textarea ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    required={required}
                    rows={3}
                    className="w-full border border-ink bg-transparent px-3 py-2 focus:outline-none focus:bg-sand-200 text-sm"
                    data-testid={testid}
                />
            ) : (
                <input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    required={required}
                    className="w-full border border-ink bg-transparent px-3 py-2 focus:outline-none focus:bg-sand-200 text-sm"
                    data-testid={testid}
                />
            )}
        </div>
    );
}

/* ---------- Visitors Tab ---------- */
function VisitorsPanel({ visitors, onRefresh }) {
    const { log, summary } = visitors;
    const formatDate = (v) => {
        if (!v) return '—';
        try {
            const d = typeof v === 'string' ? new Date(v) : new Date(v);
            return d.toLocaleString('ar-EG', {
                dateStyle: 'medium',
                timeStyle: 'short',
            });
        } catch {
            return String(v);
        }
    };

    return (
        <div className="py-10 space-y-10" data-testid="admin-visitors-panel">
            <div className="flex items-baseline justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-ink-400 mb-2">
                        — سجلّ الزوّار
                    </div>
                    <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight">
                        من زار الموقع
                    </h2>
                </div>
                <button
                    onClick={onRefresh}
                    className="text-xs border border-ink px-4 py-2 hover:bg-ink hover:text-sand-100 transition-colors"
                    data-testid="admin-refresh-visitors"
                >
                    تحديث السجل
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard
                    label="إجمالي الزيارات"
                    value={log?.total ?? '…'}
                    testid="visits-total"
                />
                <StatCard
                    label="عناوين IP فريدة"
                    value={log?.unique_ips ?? '…'}
                    accent
                    testid="visits-unique"
                />
                <StatCard
                    label="عرض في السجل"
                    value={log?.entries?.length ?? '…'}
                    testid="visits-shown"
                />
            </div>

            <div>
                <h3 className="font-display font-black text-xl md:text-2xl mb-4">
                    الزوّار حسب IP
                </h3>
                <div className="border border-ink overflow-x-auto">
                    <table
                        className="w-full text-sm"
                        data-testid="admin-visitors-summary-table"
                    >
                        <thead>
                            <tr className="bg-ink text-sand-100 text-[11px] uppercase tracking-widest">
                                <th className="text-start p-3">IP</th>
                                <th className="text-start p-3">الزيارات</th>
                                <th className="text-start p-3">آخر زيارة</th>
                                <th className="text-start p-3 hidden md:table-cell">
                                    المتصفح
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {summary.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="p-6 text-center text-ink-400"
                                    >
                                        لا توجد زيارات مسجّلة بعد.
                                    </td>
                                </tr>
                            )}
                            {summary.map((v, i) => (
                                <tr
                                    key={i}
                                    className="border-t border-ink/20 hover:bg-sand-200 transition-colors"
                                    data-testid={`visitor-row-${i}`}
                                >
                                    <td className="p-3 font-mono" dir="ltr">
                                        {v.ip}
                                    </td>
                                    <td className="p-3 font-display font-black text-emerald-signal">
                                        {v.count}
                                    </td>
                                    <td className="p-3 text-ink-600 whitespace-nowrap">
                                        {formatDate(v.last_seen)}
                                    </td>
                                    <td className="p-3 text-ink-400 text-xs truncate max-w-[300px] hidden md:table-cell">
                                        {v.user_agent || '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div>
                <h3 className="font-display font-black text-xl md:text-2xl mb-4">
                    آخر {log?.entries?.length || 0} زيارة
                </h3>
                <div className="border border-ink overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-ink text-sand-100 text-[11px] uppercase tracking-widest">
                                <th className="text-start p-3">التوقيت</th>
                                <th className="text-start p-3">IP</th>
                                <th className="text-start p-3 hidden md:table-cell">
                                    المتصفح
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {(log?.entries || []).map((e) => (
                                <tr
                                    key={e.id}
                                    className="border-t border-ink/20 hover:bg-sand-200 transition-colors"
                                >
                                    <td className="p-3 text-ink-600 whitespace-nowrap">
                                        {formatDate(e.timestamp)}
                                    </td>
                                    <td className="p-3 font-mono" dir="ltr">
                                        {e.ip}
                                    </td>
                                    <td className="p-3 text-ink-400 text-xs truncate max-w-[400px] hidden md:table-cell">
                                        {e.user_agent || '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
